/**
 * dcent-coin-assets.js — 지원 코인·토큰 데이터 모듈 (drop-03 W0).
 *
 * 원본: ~/work/homepage-design-src/src/coinAssets.ts (88줄).
 * React 훅(useSupportedAssets)은 이식하지 않는다 — 순수 함수 + Promise 만.
 *
 * 데이터 경로: 1순위 원격(assets.dcentwallet.com, CORS 허용 — 기존 dcent-coin-support.js 가
 * 이미 같은 URL 을 브라우저에서 fetch 중이라 검증된 사실) → 실패 시 테마 asset 스냅샷
 * (assets/dcent-supported-assets.json) → 모듈 캐시(remoteUrl 기준, 세션 1회).
 *
 * **URL 주입 경로는 하나다** — 소비처(브라우저 스니펫)가 렌더한 설정 island 의 값을
 * `fetchAssets({ remoteUrl, snapshotUrl })` 인자로 넘긴다. 이 테마엔 assets/*.js.liquid 가 0건이라
 * JS 안에서 Liquid 를 못 쓰고, import.meta.url 상대 해석은 Shopify asset URL 의 ?v= 버전 쿼리 때문에
 * 취약하다. window.DCENT_COIN_REMOTE_URL · DCENT_COIN_SNAPSHOT_URL 전역은 인자를 생략한 호출자를 위한
 * **폴백 경로로만** 남긴다(이번 wave 에서는 아무도 주입하지 않는다).
 *
 * 소비 계약:
 *   <script src="{{ 'dcent-coin-assets.js' | asset_url }}" type="module" defer></script>
 *   window.DcentCoinAssets.fetchAssets({ remoteUrl, snapshotUrl }).then((assets) => { ... });
 *
 * fetchAssets 가 돌려주는 wallets 는 원본 그대로가 아니다 — MAGE-321 정규화(card ⊇ bio)를
 * 거친 값이다. 근거와 제거 조건은 normalizeDcentSScope 주석 참조.
 *
 * 멱등 설치 + 최상위 return 금지 — 이유는 dcent-scroll-lock.js 헤더 주석 참조.
 *
 * 실측(2026-07-31 스냅샷): 4,959 자산 / 99 네트워크 → roundedCounts = 4,900 / 100.
 * JSON 에 features 필드는 **없다**(실측 키 = cgkId·currencyId·icon·link·name·network·symbol·tags·wallets)
 * → 이 모듈이 내장 스왑 네트워크 스냅샷(SWAP_NETWORKS — 수동 관리)으로 자산마다 features 를
 *   채워서 돌려준다(2026-08-19). 원격 스왑 데이터 실시간 연동은 쓰지 않는다.
 *
 * 양 샵 byte-identical — assets/dcent-* 는 parity 게이트 분모다.
 * 사용자 노출 문자열 중 features 라벨은 언어 무관 영문 고정이다. 기기 표시명은 로케일 키로
 * 옮겼다(아래 DEVICE_CHIP 주석 참조) — 국내몰이 국문으로 나와야 하기 때문이다.
 */

if (!window.DcentCoinAssets) {
  const REMOTE_DEFAULT =
    "https://assets.dcentwallet.com/info/dcent_supported_assets.json";

  /**
   * 표시용 기기 키. bio / card / app 은 JSON wallets 값 그대로이고,
   * **x 는 원천 데이터에 없다** — normalizeDcentXScope 가 card 를 보고 파생시킨다.
   */
  const DEVICE_KEYS = ["bio", "card", "x", "app"];
  /**
   * ⚠️ 미사용 — 표시명 SoT 는 로케일 키 `sections.dcent-page-coins-browser.device_{bio,card,app}`
   * 이고, 실제 렌더는 dcent-coins-browser.liquid 가 만든 config.deviceChip 을 쓴다.
   * 이 맵은 export 만 남아 있고 소비처가 없다. **표시명을 여기서 고치지 말 것** —
   * 고쳐도 화면은 안 바뀌고, 양 샵 byte-identical 이라 국문/영문을 여기서 가를 수도 없다.
   */
  const DEVICE_CHIP = {
    bio: "Biometric wallet",
    card: "DCENT S",
    x: "DCENT X",
    app: "App",
  };
  /** 피그마 원문 — 스왑 지원 자산의 라벨. 미지원 자산은 Swap 만 뺀다 */
  const FEATURES_LABEL = "Send · Receive · Swap";
  const FEATURES_NO_SWAP = "Send · Receive";
  /**
   * 스왑 지원 네트워크 — **수동 관리 스냅샷** (2026-08-19, 원천: 앱 스왑 백엔드
   * swap-supported.json 의 네트워크 목록. 원격 실시간 연동은 같은 날 마케팅 결정으로 끊었다 —
   * 원천 URL 비노출 + 표기 통제가 목적이고, 네트워크 목록은 자주 변하지 않는다).
   *
   * 갱신 방법: 스왑 지원 네트워크가 바뀌면 아래 배열만 고쳐 커밋한다.
   *   id        — 자산 JSON 메인넷 코인의 currencyId 와 **정확히 일치**해야 매칭된다
   *   providers — 상세 팝업 "Swap providers" 에 낼 프로바이더 id (표시명은 PROVIDER_NAME)
   */
  const SWAP_NETWORKS = [
    { id: "ETHEREUM", providers: ["butter_swap", "sushi_swap", "rubic_swap", "lifi_swap", "uniswap_swap", "squid_router", "changenow_exchange", "changelly_exchange", "exolix_exchange"] }, // Ethereum
    { id: "CHAN:10", providers: ["sushi_swap", "rubic_swap", "lifi_swap", "uniswap_swap", "squid_router", "changenow_exchange", "changelly_exchange", "exolix_exchange"] }, // Optimism
    { id: "BSC", providers: ["butter_swap", "sushi_swap", "rubic_swap", "lifi_swap", "uniswap_swap", "squid_router", "changenow_exchange", "changelly_exchange"] }, // Binance Smart Chain
    { id: "CHAN:100", providers: ["sushi_swap", "squid_router"] }, // Gnosis
    { id: "POLYGON", providers: ["butter_swap", "sushi_swap", "rubic_swap", "lifi_swap", "uniswap_swap", "squid_router", "changenow_exchange", "changelly_exchange", "exolix_exchange"] }, // Polygon
    { id: "KLAYTN", providers: ["rubic_swap", "swap_scanner", "lifi_swap", "changenow_exchange", "changelly_exchange", "exolix_exchange"] }, // Kaia
    { id: "CHAN:8453", providers: ["butter_swap", "sushi_swap", "rubic_swap", "lifi_swap", "uniswap_swap", "squid_router", "changenow_exchange", "changelly_exchange", "exolix_exchange"] }, // Base
    { id: "CHAN:42161", providers: ["butter_swap", "sushi_swap", "rubic_swap", "lifi_swap", "uniswap_swap", "squid_router", "changenow_exchange", "changelly_exchange", "exolix_exchange"] }, // Arbitrum One
    { id: "CHAN:43114", providers: ["sushi_swap", "rubic_swap", "lifi_swap", "uniswap_swap", "squid_router", "changenow_exchange", "changelly_exchange"] }, // Avalanche C-Chain
    { id: "CHAN:22776", providers: ["butter_swap"] }, // MAP Protocol
    { id: "SOLANA", providers: ["butter_swap", "lifi_swap", "changenow_exchange", "changelly_exchange", "exolix_exchange"] }, // Solana
    { id: "CHAN:25", providers: ["sushi_swap", "rubic_swap", "lifi_swap", "changenow_exchange", "exolix_exchange"] }, // Cronos
    { id: "CHAN:250", providers: ["sushi_swap", "rubic_swap", "squid_router"] }, // Fantom Opera
    { id: "CHAN:288", providers: ["sushi_swap", "rubic_swap", "lifi_swap"] }, // Boba
    { id: "CHAN:314", providers: ["sushi_swap", "rubic_swap", "squid_router", "changenow_exchange", "changelly_exchange", "exolix_exchange"] }, // Filecoin EVM
    { id: "CHAN:534352", providers: ["sushi_swap", "rubic_swap", "lifi_swap", "squid_router"] }, // Scroll
    { id: "CHAN:167000", providers: ["sushi_swap", "rubic_swap", "changelly_exchange", "exolix_exchange"] }, // Taiko Alethia
    { id: "CHAN:14", providers: ["rubic_swap", "changenow_exchange", "changelly_exchange", "exolix_exchange"] }, // Flare Network
    { id: "CHAN:66", providers: ["rubic_swap"] }, // OKX Chain
    { id: "CHAN:592", providers: ["rubic_swap"] }, // Astar EVM
    { id: "CHAN:1625", providers: ["rubic_swap", "lifi_swap"] }, // Gravity Alpha
    { id: "CHAN:42220", providers: ["rubic_swap", "lifi_swap", "uniswap_swap", "squid_router", "changenow_exchange", "changelly_exchange", "exolix_exchange"] }, // Celo
    { id: "CHAN:1666600000", providers: ["rubic_swap"] }, // Harmony
    { id: "CHAN:143", providers: ["rubic_swap", "lifi_swap"] }, // Monad Mainnet
    { id: "CHAN:146", providers: ["lifi_swap"] }, // Sonic
    { id: "CHAN:999", providers: ["lifi_swap"] }, // HyperEVM
    { id: "CHAN:1868", providers: ["lifi_swap"] }, // Soneium
    { id: "CHAN:80094", providers: ["lifi_swap"] }, // Berachain
    { id: "CHAN:98866", providers: ["lifi_swap"] }, // Plume
    { id: "XINFIN", providers: ["lifi_swap", "changenow_exchange", "changelly_exchange"] }, // XDC (XinFin)
    { id: "RIPPLE", providers: ["squid_router", "changenow_exchange", "changelly_exchange", "exolix_exchange"] }, // XRPL
    { id: "CHAN:1440000", providers: ["squid_router"] }, // XRPL EVM
    { id: "STELLAR", providers: ["squid_router", "changenow_exchange", "changelly_exchange", "exolix_exchange"] }, // Stellar
    { id: "BITCOIN", providers: ["changenow_exchange", "changelly_exchange", "exolix_exchange"] }, // Bitcoin
    { id: "TRON", providers: ["changenow_exchange", "changelly_exchange", "exolix_exchange"] }, // TRON
    { id: "ALGORAND", providers: ["changenow_exchange", "changelly_exchange", "exolix_exchange"] }, // Algorand
    { id: "DOGECOIN", providers: ["changenow_exchange", "changelly_exchange", "exolix_exchange"] }, // Dogecoin
    { id: "ZCASH", providers: ["changenow_exchange", "changelly_exchange"] }, // Zcash
    { id: "CARDANO", providers: ["changenow_exchange", "changelly_exchange", "exolix_exchange"] }, // Cardano
    { id: "BITCOINCASH", providers: ["changenow_exchange", "changelly_exchange", "exolix_exchange"] }, // Bitcoin Cash
    { id: "LITECOIN", providers: ["changenow_exchange", "changelly_exchange", "exolix_exchange"] }, // Litecoin
    { id: "HEDERA", providers: ["changenow_exchange", "changelly_exchange", "exolix_exchange"] }, // Hedera Hashgraph
    { id: "NEAR", providers: ["changenow_exchange", "changelly_exchange", "exolix_exchange"] }, // NEAR Protocol
    { id: "POLKADOT", providers: ["changenow_exchange", "changelly_exchange"] }, // Polkadot
    { id: "ETH-CLASSIC", providers: ["changenow_exchange", "changelly_exchange", "exolix_exchange"] }, // Ethereum Classic
    { id: "COSMOS", providers: ["changenow_exchange", "changelly_exchange", "exolix_exchange"] }, // Cosmos
    { id: "filecoin", providers: ["changenow_exchange", "changelly_exchange", "exolix_exchange"] }, // Filecoin
    { id: "VECHAIN", providers: ["changenow_exchange", "changelly_exchange", "exolix_exchange"] }, // VeChain
    { id: "TEZOS", providers: ["changenow_exchange", "changelly_exchange", "exolix_exchange"] }, // Tezos
    { id: "STACKS", providers: ["changenow_exchange"] }, // Stacks
    { id: "ECASH", providers: ["changenow_exchange", "changelly_exchange"] }, // eCash
    { id: "DIGIBYTE", providers: ["changenow_exchange", "changelly_exchange", "exolix_exchange"] }, // DigiByte
    { id: "CONFLUX", providers: ["changelly_exchange"] }, // Conflux
    { id: "RSK", providers: ["changelly_exchange"] }, // RSK Smart Bitcoin
    { id: "DASH", providers: ["changelly_exchange", "exolix_exchange"] }, // Dash
    { id: "PARA:000105", providers: ["changelly_exchange", "exolix_exchange"] }, // Astar
    { id: "constellation", providers: ["exolix_exchange"] }, // Constellation
  ];
  /** 프로바이더 id → 표시명(스왑 익스플로러 표기 기준). 새 id 는 아래 폴백이 대문자화해 낸다 */
  const PROVIDER_NAME = {
    butter_swap: "ButterSwap",
    sushi_swap: "SushiSwap",
    rubic_swap: "Rubic",
    lifi_swap: "LI.FI",
    uniswap_swap: "Uniswap",
    squid_router: "Squid",
    swap_scanner: "Swapscanner",
    changenow_exchange: "ChangeNOW",
    changelly_exchange: "Changelly",
    exolix_exchange: "Exolix",
  };
  function providerDisplayName(id) {
    if (PROVIDER_NAME[id]) return PROVIDER_NAME[id];
    return String(id)
      .replace(/_(swap|exchange|router)$/, "")
      .split("_")
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
      .join(" ");
  }

  const cacheByUrl = new Map();
  const pendingByUrl = new Map();

  /**
   * MAGE-321 — DCENT S(card) 지원 범위를 Biometric(bio)과 동일하게 맞추는 임시 정규화.
   *
   * 원본 SoT(assets.dcentwallet.com)의 wallets 는 아직 단종된 올인원 카드 기준이라 card 가
   * EVM 계열에만 붙어 있다(2026-08-13 실측 bio 4,973 / card 4,286 — 누락 687건·36 네트워크가
   * 전부 비-EVM). 신규 DCENT S 는 Biometric 과 같은 코인·토큰을 지원하므로 화면 표기가 실제와
   * 어긋난다. 실측상 card ⊆ bio 가 예외 없이 성립해(card 만 있고 bio 없는 자산 0건) "bio 면
   * card 도" 한 방향만 채우면 되고, card 를 떼는 방향은 필요 없다.
   *
   * 데이터가 아니라 소비 지점에서 고치는 이유: 이 모듈은 런타임에 원격 SoT 를 먼저 읽고 테마
   * 스냅샷(assets/dcent-supported-assets.json)은 실패 시 폴백이라, 스냅샷만 고치면 실제 화면에
   * 반영되지 않는다.
   *
   * 단종 올인원 카드 PDP 의 지원 코인 표(dcent-coin-support.js)는 이 정규화를 쓰지 않는다 —
   * 그 표는 실제 구형 카드의 지원 범위를 보여주는 자리라 넓히면 사실과 어긋난다.
   *
   * 지원 블록체인 그리드는 원격이 아니라 인라인 island 를 읽어 이 모듈을 공유할 수 없다.
   * chain-logo-grid.js 에 같은 이름의 쌍둥이 함수가 있으니 지울 때 함께 지운다.
   *
   * 원본이 정정되면 이 블록을 통째로 지운다. 예정 순서(2026-08-13 원태님 코멘트):
   *   1) 인터페이스 합의 — 향후 X 모델 등 모델별 조건을 담을 수 있는 스키마로
   *   2) 개발팀이 새 파일 업로드
   *   3) 리브랜딩 페이지가 새 파일 경로를 참조
   *
   * 위 1~3 은 **모델마다 분기가 필요해질 때** 착수한다. 지금은 DCENT S 범위가 Biometric 과
   * 같아 분기가 없으므로 이 정규화로 충분하다.
   */
  function normalizeDcentSScope(list) {
    return list.map((a) => {
      const wallets = a.wallets || [];
      if (!wallets.includes("bio") || wallets.includes("card")) return a;
      // 배열 순서를 그대로 렌더하는 소비처(체인 그리드 배지)가 있어 bio 바로 뒤에 넣는다.
      const next = wallets.slice();
      next.splice(next.indexOf("bio") + 1, 0, "card");
      return { ...a, wallets: next };
    });
  }

  /**
   * DCENT X 지원 범위 정규화 — **원천 데이터에 X 가 없어 card(DCENT S)에서 파생시킨다.**
   *
   * 2026-08-26 마케팅 확인: X 의 지원 코인 목록은 S 와 동일하다. 원천 JSON
   * (assets.dcentwallet.com/info/dcent_supported_assets.json)의 wallets 는 아직
   * bio / card / app 세 값뿐이라(실측 11,005 자산 전수) 표시 계층에서 채운다.
   *
   * normalizeDcentSScope 를 고치지 않고 별도 함수로 둔 이유: 그 함수는 MAGE-321
   * 게이트(mage-321-dcent-s-scope.test.ts)가 입출력을 정확히 단정하는 분모다.
   * 거기에 x 를 섞으면 S 범위 회귀 감지가 함께 무너진다.
   *
   * 적용 순서상 S 정규화 뒤에 돌아야 한다 — card 가 채워진 뒤라야 X 도 따라 붙는다.
   * 배열 순서를 그대로 렌더하는 소비처가 있어 card 바로 뒤에 넣는다.
   *
   * 원천이 모델별 스키마로 정정되면(코인 자산 모듈 상단 "예정 순서" 주석 참조)
   * 이 블록을 통째로 지운다.
   */
  function normalizeDcentXScope(list) {
    return list.map((a) => {
      const wallets = a.wallets || [];
      if (!wallets.includes("card") || wallets.includes("x")) return a;
      const next = wallets.slice();
      next.splice(next.indexOf("card") + 1, 0, "x");
      return { ...a, wallets: next };
    });
  }

  /** currencyId "BEP20/0x2170..." → 컨트랙트 주소 부분만. 메인넷 코인은 주소가 없어 null. */
  function assetAddress(a) {
    const i = a.currencyId.indexOf("/");
    return i === -1 ? null : a.currencyId.slice(i + 1);
  }

  /**
   * 스왑 지원 판정기 — **지원 네트워크의 네이티브(메인넷) 코인에만** 스왑을 표시한다.
   * 토큰은 전부 미표시(2026-08-19 마케팅 결정 — 토큰 단위 원천 데이터가 실제와 안 맞아,
   * 네트워크 단위로 넓히면 과장 표기가 된다). 자산 쪽 currencyId 는 "체인ID"(메인넷 코인)
   * 또는 "토큰접두/컨트랙트주소"(토큰)라, 체인ID 정확 일치만 보면 네이티브만 걸린다.
   * 반환: 프로바이더 표시명 배열, 미지원이면 null.
   * 실측(2026-08-19): 4,959 자산 중 네이티브 코인 57건만 스왑 표시.
   */
  const swapCheck = (() => {
    const chains = new Map(); // 체인ID → providers
    for (const c of SWAP_NETWORKS) chains.set(c.id, c.providers || []);
    return (asset) => {
      const providers = chains.get(String((asset && asset.currencyId) || ""));
      if (!providers || providers.length === 0) return null;
      return providers.map(providerDisplayName);
    };
  })();

  function fetchAssets(options) {
    const opts = options || {};
    const remoteUrl =
      opts.remoteUrl || window.DCENT_COIN_REMOTE_URL || REMOTE_DEFAULT;
    const snapshotUrl =
      opts.snapshotUrl || window.DCENT_COIN_SNAPSHOT_URL || "";

    const cached = cacheByUrl.get(remoteUrl);
    if (cached) return Promise.resolve(cached);

    let pending = pendingByUrl.get(remoteUrl);
    if (!pending) {
      pending = fetch(remoteUrl)
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .catch(() => {
          if (!snapshotUrl)
            throw new Error("dcent-coin-assets: no snapshot url");
          return fetch(snapshotUrl).then((r) => r.json());
        })
        .then((data) => {
          const list = normalizeDcentXScope(
            normalizeDcentSScope(Array.isArray(data) ? data : []),
          );
          // 스왑 지원 여부·프로바이더를 자산에 새긴다 — 내장 스냅샷(SWAP_NETWORKS) 기준.
          const out = list.map((a) => {
            const providers = swapCheck(a);
            return {
              ...a,
              features: providers ? FEATURES_LABEL : FEATURES_NO_SWAP,
              swapProviders: providers || [],
            };
          });
          cacheByUrl.set(remoteUrl, out);
          return out;
        })
        .catch(() => {
          // 실패는 캐시하지 않는다 — 다음 호출에서 재시도한다.
          pendingByUrl.delete(remoteUrl);
          return [];
        });
      pendingByUrl.set(remoteUrl, pending);
    }
    return pending;
  }

  /** 총 자산 수 / 네트워크(메인넷) 수. 로딩 전(빈 배열)이면 null. */
  function assetCounts(assets) {
    if (!assets || assets.length === 0) return null;
    const networks = new Set();
    for (const a of assets) networks.add(a.network);
    return { total: assets.length, networks: networks.size };
  }

  /** 표시용 반올림 — 토큰 수는 백 단위 내림(4,959→4,900), 네트워크 수는 십 단위 반올림(99→100). */
  function roundedCounts(counts) {
    if (!counts) return null;
    return {
      total: Math.floor(counts.total / 100) * 100,
      networks: Math.round(counts.networks / 10) * 10,
    };
  }

  window.DcentCoinAssets = {
    DEVICE_KEYS,
    DEVICE_CHIP,
    FEATURES_LABEL,
    FEATURES_NO_SWAP,
    assetAddress,
    fetchAssets,
    assetCounts,
    roundedCounts,
  };
}

export function fetchAssets(options) {
  return window.DcentCoinAssets.fetchAssets(options);
}

export function assetAddress(a) {
  return window.DcentCoinAssets.assetAddress(a);
}

export function assetCounts(assets) {
  return window.DcentCoinAssets.assetCounts(assets);
}

export function roundedCounts(counts) {
  return window.DcentCoinAssets.roundedCounts(counts);
}
